import { Button, Form, Input, Modal, Skeleton } from "antd";

import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import { toast } from "react-toastify";
import { AiOutlineDownload, AiOutlineSearch } from "react-icons/ai";
import { BsKey, BsPeople, BsPlusLg, BsShieldLock } from "react-icons/bs";
import { Link } from "react-router-dom";
import UserPrivateComponent from "../PrivateRoutes/UserPrivateComponent";
import { addRole, loadAllRole } from "../../redux/rtk/features/role/roleSlice";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";

const roleAccents = [
	"from-[#023f88] to-[#0b5cae]",
	"from-sky-500 to-blue-600",
	"from-indigo-500 to-violet-600",
	"from-amber-500 to-orange-500",
	"from-rose-500 to-pink-600",
];

const AddRole = ({ onAdded }) => {
	const [loader, setLoader] = useState(false);
	const dispatch = useDispatch();
	const [form] = Form.useForm();

	const onFinish = async (values) => {
		setLoader(true);
		const resp = await dispatch(addRole(values));
		setLoader(false);

		if (resp.payload.message === "success") {
			form.resetFields();
			dispatch(loadAllRole());
			onAdded && onAdded();
		}
	};

	const onFinishFailed = () => {
		toast.warning("Failed at adding role");
		setLoader(false);
	};

	return (
		<Form
			form={form}
			layout='vertical'
			requiredMark={false}
			onFinish={onFinish}
			onFinishFailed={onFinishFailed}
			autoComplete='off'>
			<Form.Item
				label={<span className='font-medium text-slate-700'>Role name</span>}
				name='name'
				rules={[
					{
						required: true,
						message: "Please input role name!",
					},
				]}>
				<Input size='large' placeholder='e.g. Coordinator' />
			</Form.Item>

			<Button
				type='primary'
				size='large'
				htmlType='submit'
				block
				loading={loader}
				className='cp-navy-btn rounded-xl'>
				Add New Role
			</Button>
		</Form>
	);
};

const StatCard = ({ icon: Icon, label, value, accent }) => (
	<div className='flex items-center gap-4 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200/70'>
		<div
			className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${accent} text-white shadow-md`}>
			<Icon className='text-2xl' />
		</div>
		<div>
			<p className='text-3xl font-extrabold leading-none text-slate-900'>
				{value}
			</p>
			<p className='mt-1 text-sm font-medium text-slate-600'>{label}</p>
		</div>
	</div>
);

const RoleCard = ({ role, index }) => {
	const permissionCount = role._count?.rolePermission;
	const userCount =
		role._count && (role._count.user || 0) + (role._count.customer || 0);

	return (
		<div className='group flex flex-col rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200/70 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl'>
			<div className='flex items-start justify-between'>
				<div
					className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${
						roleAccents[index % roleAccents.length]
					} text-white shadow-md transition-transform duration-300 group-hover:scale-110`}>
					<BsShieldLock className='text-2xl' />
				</div>
				<span
					className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ring-1 ${
						role.status === false
							? "bg-red-100 text-red-700 ring-red-200"
							: "bg-green-100 text-green-800 ring-green-200"
					}`}>
					<span
						className={`h-1.5 w-1.5 rounded-full ${
							role.status === false ? "bg-red-500" : "bg-green-500"
						}`}
					/>
					{role.status === false ? "Inactive" : "Active"}
				</span>
			</div>

			<h3 className='mt-5 text-lg font-bold capitalize text-slate-900'>
				{role.name}
			</h3>
			<p className='text-sm text-slate-600'>
				Created {dayjs(role.createdAt).format("DD MMM YYYY")}
			</p>

			{role._count && (
				<div className='mt-4 flex gap-3'>
					<div className='flex-1 rounded-xl bg-slate-50 px-3 py-2'>
						<p className='flex items-center gap-1.5 text-xs font-medium text-slate-600'>
							<BsKey /> Permissions
						</p>
						<p className='text-lg font-bold text-slate-900'>{permissionCount}</p>
					</div>
					<div className='flex-1 rounded-xl bg-slate-50 px-3 py-2'>
						<p className='flex items-center gap-1.5 text-xs font-medium text-slate-600'>
							<BsPeople /> Users
						</p>
						<p className='text-lg font-bold text-slate-900'>{userCount}</p>
					</div>
				</div>
			)}

			<div className='mt-5 flex gap-2'>
				<UserPrivateComponent permission={"readSingle-role"}>
					<Link
						to={`/admin/setup/role/${role.id}`}
						className='flex-1 rounded-xl border border-[#023f88] px-3 py-2 text-center text-sm font-semibold text-[#023f88] transition hover:bg-[#023f88] hover:text-white'>
						View details
					</Link>
				</UserPrivateComponent>
				<UserPrivateComponent permission={"create-rolePermission"}>
					<Link
						to={`/admin/setup/role/permit/${role.id}`}
						className='flex-1 rounded-xl bg-gradient-to-r from-[#023f88] to-[#0b5cae] px-3 py-2 text-center text-sm font-semibold text-white shadow transition hover:opacity-90 hover:text-white'>
						Permissions
					</Link>
				</UserPrivateComponent>
			</div>
		</div>
	);
};

const Role = () => {
	const dispatch = useDispatch();
	const { list } = useSelector((state) => state.role);
	const [search, setSearch] = useState("");
	const [addOpen, setAddOpen] = useState(false);

	useEffect(() => {
		dispatch(loadAllRole());
	}, [dispatch]);

	const roles = useMemo(() => list || [], [list]);
	const filteredRoles = useMemo(() => {
		const term = search.trim().toLowerCase();
		return term
			? roles.filter((r) => r.name?.toLowerCase().includes(term))
			: roles;
	}, [roles, search]);

	const hasCounts = roles.some((r) => r._count);
	const totalUsers = roles.reduce(
		(sum, r) => sum + (r._count?.user || 0) + (r._count?.customer || 0),
		0
	);
	const activeRoles = roles.filter((r) => r.status !== false).length;

	return (
		<UserPrivateComponent permission={"readAll-role"}>
			<div className='max-w-7xl mx-auto py-6 md:py-8'>
				{/* Header */}
				<div className='relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#011f44] via-[#023f88] to-[#0b5cae] px-6 py-8 md:px-10 text-white shadow-xl'>
					<div className='absolute -top-16 -right-16 h-56 w-56 rounded-full bg-white/10 blur-2xl' />
					<div className='absolute -bottom-20 left-1/3 h-48 w-48 rounded-full bg-sky-300/20 blur-2xl' />

					<div className='relative flex flex-col gap-6 md:flex-row md:items-center md:justify-between'>
						<div>
							<span className='inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-medium uppercase tracking-wide backdrop-blur-sm'>
								<BsShieldLock />
								Access Control
							</span>
							<h1 className='mt-4 text-3xl md:text-4xl font-extrabold tracking-tight text-white'>
								Roles & Permissions
							</h1>
							<p className='mt-2 max-w-xl text-blue-50/90'>
								Create roles and decide exactly what each one is allowed to do.
							</p>
						</div>

						<div className='flex flex-wrap gap-3'>
							<UserPrivateComponent permission={"create-role"}>
								<button
									type='button'
									onClick={() => setAddOpen(true)}
									className='inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-[#023f88] shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl'>
									<BsPlusLg />
									Add Role
								</button>
							</UserPrivateComponent>
							{roles.length > 0 && (
								<CSVLink
									data={roles.map(({ _count, ...r }) => r)}
									filename='roles'
									className='inline-flex items-center gap-2 rounded-xl bg-white/15 px-4 py-2.5 text-sm font-semibold text-white backdrop-blur-sm transition hover:bg-white/25 hover:text-white'>
									<AiOutlineDownload className='text-lg' />
									Download CSV
								</CSVLink>
							)}
						</div>
					</div>
				</div>

				{/* Stats */}
				<div className={`mt-6 grid gap-4 ${hasCounts ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
					<StatCard
						icon={BsShieldLock}
						label='Total roles'
						value={roles.length}
						accent='from-[#023f88] to-[#0b5cae]'
					/>
					<StatCard
						icon={BsKey}
						label='Active roles'
						value={activeRoles}
						accent='from-green-500 to-green-600'
					/>
					{hasCounts && (
						<StatCard
							icon={BsPeople}
							label='Users assigned'
							value={totalUsers}
							accent='from-indigo-500 to-violet-600'
						/>
					)}
				</div>

				{/* Search */}
				<div className='mt-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between'>
					<div>
						<h2 className='text-xl md:text-2xl font-bold text-slate-900'>All roles</h2>
						<p className='text-sm text-slate-600'>
							{filteredRoles.length} of {roles.length} roles
						</p>
					</div>
					<Input
						allowClear
						size='large'
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						prefix={<AiOutlineSearch className='text-slate-400' />}
						placeholder='Search roles…'
						className='rounded-xl sm:max-w-xs'
					/>
				</div>

				{/* Roles */}
				{!list ? (
					<div className='mt-5 grid gap-5 sm:grid-cols-2 xl:grid-cols-3'>
						{[0, 1, 2].map((i) => (
							<div key={i} className='rounded-2xl bg-white p-6 ring-1 ring-slate-200/70'>
								<Skeleton active />
							</div>
						))}
					</div>
				) : filteredRoles.length ? (
					<div className='mt-5 grid gap-5 sm:grid-cols-2 xl:grid-cols-3'>
						{filteredRoles.map((role, i) => (
							<RoleCard key={role.id} role={role} index={i} />
						))}
					</div>
				) : (
					<div className='mt-5 rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center'>
						<BsShieldLock className='mx-auto text-4xl text-slate-300' />
						<p className='mt-3 font-semibold text-slate-700'>No roles found</p>
						<p className='mt-1 text-sm text-slate-600'>
							Try a different search or add a new role.
						</p>
					</div>
				)}
			</div>

			<Modal
				open={addOpen}
				onCancel={() => setAddOpen(false)}
				footer={null}
				centered
				destroyOnClose
				title={
					<div className='flex items-center gap-3'>
						<div className='flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#023f88] to-[#0b5cae] text-white'>
							<BsPlusLg />
						</div>
						<div>
							<p className='text-lg font-bold text-slate-900'>Add new role</p>
							<p className='text-sm font-normal text-slate-600'>
								You can assign permissions after creating it.
							</p>
						</div>
					</div>
				}>
				<div className='pt-2'>
					<AddRole onAdded={() => setAddOpen(false)} />
				</div>
			</Modal>
		</UserPrivateComponent>
	);
};

export default Role;
